import { CircleCheck, Loader2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { SubmitButton, SubmitButtonIcon, SubmitButtonSpinner } from './styled';

export interface AuthSubmitButtonProps {
  children: ReactNode;
  isLoading?: boolean;
  confirmed?: boolean;
  disabled?: boolean;
  type?: 'button' | 'submit';
  onClick?: () => void;
}

/**
 * Primary button for authentication forms with loading and confirmed states.
 *
 * Supports two modes:
 * - **Form submit** (default): type="submit" - triggers form's onSubmit handler
 * - **Action button**: type="button" + onClick - executes custom logic without form submission
 *
 * @component
 * @example
 * ```tsx
 * // Form submit button (default)
 * <AuthSubmitButton isLoading={loading}>
 *   Sign In
 * </AuthSubmitButton>
 *
 * // Action button with click handler
 * <AuthSubmitButton type="button" onClick={handleClick} isLoading={loading}>
 *   Verify Code
 * </AuthSubmitButton>
 * ```
 */
export function AuthSubmitButton({
  children,
  isLoading = false,
  confirmed = false,
  disabled = false,
  type = 'submit',
  onClick,
}: AuthSubmitButtonProps) {
  return (
    <SubmitButton type={type} disabled={disabled || isLoading || confirmed} onClick={onClick}>
      {isLoading && (
        <SubmitButtonSpinner>
          <Loader2 size={20} />
        </SubmitButtonSpinner>
      )}
      {confirmed && (
        <SubmitButtonIcon>
          <CircleCheck size={20} />
        </SubmitButtonIcon>
      )}
      {children}
    </SubmitButton>
  );
}
